import React, { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import { ScrollArea } from "../components/ui/scroll-area";
import { sendChat } from "../lib/api";

const POWDER = "https://static.prod-images.emergentagent.com/jobs/b676cfa9-b99a-467a-b748-e01e0fba16e9/images/5c97f9724bfdaad482043e8f51261955fa7fece6ffe9ee3b63347e7b3c7de85d.png";

const SUGGESTIONS = [
  "Which brush is best for blending eyeshadow?",
  "How do I get a natural-looking contour?",
  "How often should I wash my brushes?",
  "What's the difference between a kabuki and a stippling brush?",
];

export default function AskExpert({ open, onOpenChange }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi — I'm your BrushBasics Expert. Ask me anything about which brush to use, application techniques, or brush care.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const res = await sendChat({ session_id: sessionId, message: msg });
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I'm having trouble reaching the studio right now. Please try again in a moment.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="expert-sheet"
        side="right"
        className="w-full sm:max-w-md p-0 bg-[#F9F7F3] border-l border-[#E5E0D8] flex flex-col"
      >
        <div
          className="relative px-6 pt-8 pb-6 border-b border-[#E5E0D8]"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(249,247,243,0.85), rgba(249,247,243,0.95)), url(${POWDER})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <SheetHeader className="space-y-2 text-left">
            <div className="overline">Live Atelier</div>
            <SheetTitle className="font-serif-display text-3xl font-light text-stone-900">
              Ask the Brush Expert
            </SheetTitle>
            <SheetDescription className="text-sm text-stone-600 leading-relaxed">
              A makeup artist's brain at your fingertips. Ask anything about brushes, technique, or care.
            </SheetDescription>
          </SheetHeader>
        </div>

        <ScrollArea className="flex-1" data-testid="chat-scroll">
          <div ref={scrollRef} className="px-6 py-6 space-y-4">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#E8D5D1] text-stone-900"
                      : "bg-white border border-[#E5E0D8] text-stone-700"
                  }`}
                >
                  {m.content.split("\n").map((line, idx) => (
                    <p key={idx} className={idx === 0 ? "" : "mt-2"}>{line}</p>
                  ))}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chat-loading">
                <div className="bg-white border border-[#E5E0D8] px-4 py-3 text-sm text-stone-500">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-stone-400 animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-stone-400 animate-pulse [animation-delay:120ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-stone-400 animate-pulse [animation-delay:240ms]" />
                  </span>
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="pt-2">
                <div className="overline mb-3">Try asking</div>
                <div className="flex flex-col gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`chat-suggest-${i}`}
                      onClick={() => send(s)}
                      className="text-left text-sm text-stone-700 bg-white border border-[#E5E0D8] hover:border-[#8C6A5D] hover:text-stone-900 px-4 py-3 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="border-t border-[#E5E0D8] p-4 bg-white">
          <div className="flex items-end gap-2">
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              rows={2}
              placeholder="Ask about a brush, a look, or a technique…"
              className="flex-1 resize-none text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-2 leading-relaxed"
            />
            <button
              data-testid="chat-send-btn"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="bg-[#8C6A5D] disabled:bg-stone-300 hover:bg-[#70544A] text-white text-xs uppercase tracking-[0.2em] px-4 py-3 transition-colors"
            >
              Send
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
